import { useRef, useState } from "react";
import {
  allowedActions, blankAction, defaultConfig, defaultVariables, durationText, roleNames, splitMinutes, weekdayNames,
  type ActionType, type RuleAction, type RuleConfig, type RuleOptions, type Trigger,
} from "../lib/automations";
import { Field } from "./ui";
import { DIALECTS } from "./AgentBuilder";

// The rule editor is shared by a company's Automations page and the
// operator's industry template editor ("template" mode: no company-specific
// choices such as services, stages, people or outside addresses).

export interface RuleDraft {
  key?: string;
  name: string;
  enabled: boolean;
  trigger: Trigger;
  trigger_config: RuleConfig;
  actions: RuleAction[];
}

type Words = ((term: "contact" | "deal" | "appointment" | "service" | "resource", plural?: boolean) => string) & {
  lower: (term: "contact" | "deal" | "appointment" | "service" | "resource", plural?: boolean) => string;
};

export const triggerOrder: Trigger[] = [
  "appointment_booked", "appointment_before", "appointment_after", "appointment_rescheduled", "appointment_cancelled",
  "appointment_completed", "appointment_no_show", "daily", "contact_created", "deal_stage_changed",
];

// withArticle: "a meeting", "an opportunity".
export const withArticle = (word: string) => (/^[aeiou]/i.test(word) ? "an " : "a ") + word;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function triggerLabel(t: Trigger, w: Words): string {
  const a = withArticle(w.lower("appointment"));
  switch (t) {
    case "appointment_booked": return `${cap(a)} is booked`;
    case "appointment_rescheduled": return `${cap(a)} is moved to another time`;
    case "appointment_cancelled": return `${cap(a)} is cancelled`;
    case "appointment_completed": return `${cap(a)} is marked attended`;
    case "appointment_no_show": return `Someone misses their ${w.lower("appointment")}`;
    case "appointment_before": return `Before ${a}`;
    case "appointment_after": return `After ${a}`;
    case "deal_stage_changed": return `${cap(withArticle(w.lower("deal")))} moves stage`;
    case "contact_created": return `A new ${w.lower("contact")} is added`;
    case "daily": return "Every day at a set time";
  }
}

export const actionLabel: Record<ActionType, string> = {
  message_customer: "Message the customer",
  notify_team: "Email the team",
  create_task: "Create a task",
  daily_digest: "Email the day's schedule",
};

// describe gives a one-line summary of a rule for lists.
export function describe(r: RuleDraft, w: Words): string {
  const c = r.trigger_config;
  let when = triggerLabel(r.trigger, w);
  if (r.trigger === "appointment_before" && c.minutes) when = `${durationText(c.minutes)} before ${withArticle(w.lower("appointment"))}`;
  if (r.trigger === "appointment_after" && c.minutes) when = `${durationText(c.minutes)} after ${c.only_attended ? "an attended " + w.lower("appointment") : withArticle(w.lower("appointment"))}`;
  if (r.trigger === "daily") {
    const days = (c.weekdays ?? []).length === 7 ? "every day" : (c.weekdays ?? []).map((d) => weekdayNames[d]).join(", ");
    when = `At ${c.time} (${days})`;
  }
  return `${when} → ${r.actions.map((a) => actionLabel[a.type].toLowerCase()).join(", ")}`;
}

// Translator drafts the Arabic version of a message template with AI.
export type Translator = (text: string, kind: "text" | "subject", trigger: Trigger, dialect: string) => Promise<string>;

export function RuleEditor({ value, onChange, options, mode, words, canSetExternal = false, externalLimit = 0, translate }: {
  value: RuleDraft;
  onChange: (v: RuleDraft) => void;
  options?: RuleOptions;
  mode: "company" | "template";
  words: Words;
  canSetExternal?: boolean;
  externalLimit?: number;
  translate?: Translator;
}) {
  const r = value;
  const set = (patch: Partial<RuleDraft>) => onChange({ ...r, ...patch });
  const cfg = r.trigger_config;
  const setCfg = (patch: Partial<RuleConfig>) => set({ trigger_config: { ...cfg, ...patch } });
  const vars = options?.variables?.[r.trigger] ?? defaultVariables(r.trigger);
  const company = mode === "company";

  const changeTrigger = (t: Trigger) => {
    const allowed = allowedActions(t);
    const kept = r.actions.filter((a) => allowed.includes(a.type));
    set({ trigger: t, trigger_config: defaultConfig(t), actions: kept.length ? kept : [blankAction(allowed[0])] });
  };
  const setAction = (i: number, a: RuleAction) => set({ actions: r.actions.map((x, j) => (j === i ? a : x)) });

  return (
    <div className="rule-editor">
      <div className="row">
        <Field label="Name"><input value={r.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Booking confirmation" /></Field>
        {mode === "template" && (
          <Field label="Key"><input value={r.key ?? ""} maxLength={41} onChange={(e) => set({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="e.g. morning_schedule" /></Field>
        )}
      </div>
      <label className="check"><input type="checkbox" checked={r.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> {mode === "template" ? "Switched on when added to a company" : "On"}</label>

      <fieldset className="rule-block">
        <legend>When</legend>
        <select aria-label="When" value={r.trigger} onChange={(e) => changeTrigger(e.target.value as Trigger)}>
          {triggerOrder.map((t) => <option key={t} value={t}>{triggerLabel(t, words)}</option>)}
        </select>
        {(r.trigger === "appointment_before" || r.trigger === "appointment_after") && (
          <Duration minutes={cfg.minutes ?? 60} onChange={(m) => setCfg({ minutes: m })} suffix={r.trigger === "appointment_before" ? `before it starts` : `after it ends`} />
        )}
        {r.trigger === "appointment_after" && (
          <label className="check"><input type="checkbox" checked={!!cfg.only_attended} onChange={(e) => setCfg({ only_attended: e.target.checked })} /> Only if it was marked attended</label>
        )}
        {r.trigger === "daily" && (
          <>
            <div className="inline">
              <span>At</span>
              <input aria-label="Time" type="time" value={cfg.time ?? "07:30"} onChange={(e) => setCfg({ time: e.target.value })} style={{ width: "auto" }} />
            </div>
            <div className="checks" role="group" aria-label="Days">
              {weekdayNames.map((d, i) => (
                <label key={d}><input type="checkbox" checked={(cfg.weekdays ?? []).includes(i)}
                  onChange={(e) => setCfg({ weekdays: e.target.checked ? [...(cfg.weekdays ?? []), i].sort() : (cfg.weekdays ?? []).filter((x) => x !== i) })} /> {d}</label>
              ))}
            </div>
          </>
        )}
        {company && r.trigger.startsWith("appointment_") && (options?.services.length ?? 0) > 0 && (
          <Picker label={`Only for these ${w2(words, "service", true)} (none ticked = all)`} items={options!.services} value={cfg.service_ids ?? []} onChange={(ids) => setCfg({ service_ids: ids })} />
        )}
        {company && r.trigger === "deal_stage_changed" && options && (
          <>
            <Field label="Pipeline">
              <select value={cfg.pipeline_id ?? ""} onChange={(e) => setCfg({ pipeline_id: e.target.value || null, stage_ids: [] })}>
                <option value="">Any pipeline</option>
                {options.pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            {cfg.pipeline_id && (
              <Picker label="Only when it moves into (none ticked = any stage)" items={options.pipelines.find((p) => p.id === cfg.pipeline_id)?.stages ?? []}
                value={cfg.stage_ids ?? []} onChange={(ids) => setCfg({ stage_ids: ids })} />
            )}
          </>
        )}
        {r.trigger === "contact_created" && (
          <Picker label="Only when they came from (none ticked = anywhere; imports never count)"
            items={[{ id: "manual", name: "Added by staff" }, { id: "website_agent", name: "Website chat" }, { id: "whatsapp", name: "WhatsApp" }, { id: "booking_page", name: "Booking page" }]}
            value={cfg.sources ?? []} onChange={(ids) => setCfg({ sources: ids })} />
        )}
      </fieldset>

      {r.actions.map((a, i) => (
        <fieldset key={i} className="rule-block">
          <legend>{i === 0 ? "Then" : "And"}</legend>
          <div className="inline">
            <select aria-label={`Action ${i + 1}`} value={a.type} onChange={(e) => setAction(i, blankAction(e.target.value as ActionType))}>
              {allowedActions(r.trigger).map((t) => <option key={t} value={t}>{actionLabel[t]}</option>)}
            </select>
            <span className="spacer" />
            {r.actions.length > 1 && <button type="button" className="link small" onClick={() => set({ actions: r.actions.filter((_, j) => j !== i) })}>Remove</button>}
          </div>
          <ActionFields a={a} onChange={(x) => setAction(i, x)} vars={vars} options={options} company={company} trigger={r.trigger}
            canSetExternal={canSetExternal} externalLimit={externalLimit} words={words} translate={translate} />
        </fieldset>
      ))}
      {r.actions.length < 5 && (
        <button type="button" className="secondary" onClick={() => {
          const allowed = allowedActions(r.trigger);
          const next = allowed.find((t) => !r.actions.some((x) => x.type === t)) ?? allowed[0];
          set({ actions: [...r.actions, blankAction(next)] });
        }}>
          Add another action
        </button>
      )}
    </div>
  );
}

const w2 = (w: Words, t: "service", plural: boolean) => w.lower(t, plural);

function Duration({ minutes, onChange, suffix }: { minutes: number; onChange: (m: number) => void; suffix: string }) {
  const { value, unit } = splitMinutes(minutes);
  const factor = { minutes: 1, hours: 60, days: 1440 };
  return (
    <div className="inline">
      <input aria-label="How long" type="number" min={1} value={value} onChange={(e) => onChange(Math.max(1, Math.round(Number(e.target.value) || 0)) * factor[unit])} />
      <select aria-label="Unit" value={unit} onChange={(e) => onChange(value * factor[e.target.value as keyof typeof factor])} style={{ width: "auto" }}>
        <option value="minutes">minutes</option><option value="hours">hours</option><option value="days">days</option>
      </select>
      <span>{suffix}</span>
    </div>
  );
}

function Picker({ label, items, value, onChange }: { label: string; items: { id: string; name: string }[]; value: string[]; onChange: (ids: string[]) => void }) {
  return (
    <div className="field">
      <span>{label}</span>
      <div className="checks">
        {items.map((it) => (
          <label key={it.id}><input type="checkbox" checked={value.includes(it.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, it.id] : value.filter((x) => x !== it.id))} /> {it.name}</label>
        ))}
      </div>
    </div>
  );
}

// TextWithVars is a text box with buttons that insert {{variables}} where
// the cursor is.
function TextWithVars({ label, value, onChange, vars, multiline, max, dir = "auto" }: { label: string; value: string; onChange: (v: string) => void; vars: string[]; multiline?: boolean; max: number; dir?: "auto" | "rtl" }) {
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const insert = (v: string) => {
    const el = ref.current;
    const token = `{{${v}}}`;
    if (!el) return onChange(value + token);
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };
  return (
    <div className="field">
      <span>{label}</span>
      {multiline
        ? <textarea ref={ref} rows={3} maxLength={max} value={value} onChange={(e) => onChange(e.target.value)} dir={dir} />
        : <input ref={ref} maxLength={max} value={value} onChange={(e) => onChange(e.target.value)} dir={dir} />}
      <div className="var-chips">
        {vars.map((v) => <button key={v} type="button" className="chip small" onClick={() => insert(v)}>{`{{${v}}}`}</button>)}
      </div>
    </div>
  );
}

function Recipients({ a, onChange, options, company, allowAssigned, assignedLabel }: {
  a: RuleAction; onChange: (a: RuleAction) => void; options?: RuleOptions; company: boolean; allowAssigned: boolean; assignedLabel: string;
}) {
  const roles = a.roles ?? [];
  return (
    <>
      {allowAssigned && (
        <label className="check"><input type="checkbox" checked={!!a.assigned} onChange={(e) => onChange({ ...a, assigned: e.target.checked })} /> {assignedLabel}</label>
      )}
      <div className="field">
        <span>Everyone who is</span>
        <div className="checks">
          {Object.entries(roleNames).map(([k, label]) => (
            <label key={k}><input type="checkbox" checked={roles.includes(k)} onChange={(e) => onChange({ ...a, roles: e.target.checked ? [...roles, k] : roles.filter((x) => x !== k) })} /> {label}</label>
          ))}
        </div>
      </div>
      {company && options && options.members.length > 0 && (
        <Picker label="These people" items={options.members} value={a.member_ids ?? []} onChange={(ids) => onChange({ ...a, member_ids: ids })} />
      )}
    </>
  );
}

function ActionFields({ a, onChange, vars, options, company, trigger, canSetExternal, externalLimit, words, translate }: {
  a: RuleAction; onChange: (a: RuleAction) => void; vars: string[]; options?: RuleOptions; company: boolean; trigger: Trigger;
  canSetExternal: boolean; externalLimit: number; words: Words; translate?: Translator;
}) {
  const involved = trigger.startsWith("appointment_") ? `The ${words.lower("resource")} for the ${words.lower("appointment")}` : trigger === "deal_stage_changed" ? `The ${words.lower("deal")}'s owner` : `The ${words.lower("contact")}'s owner`;
  switch (a.type) {
    case "message_customer": {
      const tpl = options?.whatsapp_templates.find((t) => t.id === a.whatsapp_template_id);
      const tvars = a.template_vars ?? [];
      return (
        <>
          <Field label="Send by">
            <select value={a.channel ?? "auto"} onChange={(e) => onChange({ ...a, channel: e.target.value as RuleAction["channel"] })}>
              <option value="auto">WhatsApp if possible, otherwise email</option>
              <option value="whatsapp">WhatsApp only</option>
              <option value="email">Email only</option>
            </select>
          </Field>
          <TextWithVars label="Message" value={a.text ?? ""} onChange={(v) => onChange({ ...a, text: v })} vars={vars} multiline max={1000} />
          {a.channel !== "whatsapp" && (
            <TextWithVars label="Email subject" value={a.email_subject ?? ""} onChange={(v) => onChange({ ...a, email_subject: v })} vars={vars} max={150} />
          )}
          <ArabicVersion a={a} onChange={onChange} vars={vars} trigger={trigger} translate={translate} />
          {company && a.channel !== "email" && (
            <>
              <Field label="WhatsApp template (outside the 24-hour window)">
                <select value={a.whatsapp_template_id ?? ""} onChange={(e) => {
                  const t = options?.whatsapp_templates.find((x) => x.id === e.target.value);
                  onChange({ ...a, whatsapp_template_id: e.target.value || null, template_vars: t ? Array.from({ length: t.variable_count }, (_, i) => tvars[i] ?? vars[Math.min(i + 1, vars.length - 1)]) : [] });
                }}>
                  <option value="">None: only customers who messaged in the last 24 hours get WhatsApp</option>
                  {options?.whatsapp_templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.language}</option>)}
                </select>
              </Field>
              {tpl && tpl.variable_count > 0 && (
                <div className="row">
                  {Array.from({ length: tpl.variable_count }, (_, i) => (
                    <Field key={i} label={`Template {{${i + 1}}}`}>
                      <select value={tvars[i] ?? ""} onChange={(e) => { const n = [...tvars]; n[i] = e.target.value; onChange({ ...a, template_vars: n }); }}>
                        {vars.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </Field>
                  ))}
                </div>
              )}
            </>
          )}
          <p className="muted small">Only service messages about their {words.lower("appointment")} or account. Customers who opted out of email don't get emails.</p>
        </>
      );
    }
    case "notify_team":
      return (
        <>
          <Recipients a={a} onChange={onChange} options={options} company={company} allowAssigned={trigger !== "daily"} assignedLabel={involved} />
          <TextWithVars label="Email subject" value={a.email_subject ?? ""} onChange={(v) => onChange({ ...a, email_subject: v })} vars={vars} max={150} />
          <TextWithVars label="Message" value={a.text ?? ""} onChange={(v) => onChange({ ...a, text: v })} vars={vars} multiline max={1000} />
        </>
      );
    case "create_task":
      return (
        <>
          <TextWithVars label="Task" value={a.title ?? ""} onChange={(v) => onChange({ ...a, title: v })} vars={vars} max={200} />
          <div className="row">
            <Field label="Due in (hours)"><input type="number" min={0} max={720} value={a.due_hours ?? ""} placeholder="No due date"
              onChange={(e) => onChange({ ...a, due_hours: e.target.value === "" ? null : Math.round(Number(e.target.value)) })} /></Field>
            <Field label="Priority">
              <select value={a.priority ?? "normal"} onChange={(e) => onChange({ ...a, priority: e.target.value as RuleAction["priority"] })}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
              </select>
            </Field>
            <Field label="For">
              <select value={a.member_ids?.[0] ?? (a.assigned ? "involved" : "")} onChange={(e) => {
                const v = e.target.value;
                onChange({ ...a, assigned: v === "involved", member_ids: v && v !== "involved" ? [v] : [] });
              }}>
                <option value="involved">{involved}</option>
                <option value="">Nobody in particular</option>
                {company && options?.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
          </div>
        </>
      );
    case "daily_digest": {
      const emails = a.emails ?? [];
      return (
        <>
          <Field label="Which day">
            <select value={a.day ?? "today"} onChange={(e) => onChange({ ...a, day: e.target.value as RuleAction["day"] })}>
              <option value="today">Today's {words.lower("appointment", true)}</option>
              <option value="tomorrow">Tomorrow's {words.lower("appointment", true)}</option>
            </select>
          </Field>
          <Recipients a={a} onChange={onChange} options={options} company={company} allowAssigned={false} assignedLabel="" />
          <label className="check"><input type="checkbox" checked={!!a.only_own} onChange={(e) => onChange({ ...a, only_own: e.target.checked })} /> Each person only gets their own {words.lower("appointment", true)}</label>
          {company && (canSetExternal || emails.length > 0) && externalLimit > 0 && (
            <Field label={`Also send to these outside addresses (up to ${externalLimit}; admins only)`}>
              <EmailList value={emails} disabled={!canSetExternal} onChange={(list) => onChange({ ...a, emails: list })} />
            </Field>
          )}
          <TextWithVars label="Note at the top (optional)" value={a.text ?? ""} onChange={(v) => onChange({ ...a, text: v })} vars={vars} multiline max={1000} />
          <p className="muted small">No email is sent on days with nothing booked. Outside addresses get names, times and {words.lower("service", true)} only, no phone numbers.</p>
        </>
      );
    }
  }
}

// EmailList keeps what's typed (new lines, commas) while passing the parsed
// addresses up.
function EmailList({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled: boolean }) {
  const [raw, setRaw] = useState(value.join("\n"));
  return (
    <textarea rows={2} disabled={disabled} value={raw} placeholder="assistant@partner.com"
      onChange={(e) => { setRaw(e.target.value); onChange(e.target.value.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean)); }} />
  );
}

// ArabicVersion is the optional Arabic message for customers whose language
// is Arabic. It can be drafted with AI (company mode) and then edited.
function ArabicVersion({ a, onChange, vars, trigger, translate }: {
  a: RuleAction; onChange: (a: RuleAction) => void; vars: string[]; trigger: Trigger; translate?: Translator;
}) {
  const [open, setOpen] = useState(!!a.text_ar);
  const [dialect, setDialect] = useState("gulf");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailToo = a.channel !== "whatsapp";
  if (!open) {
    return <button type="button" className="link small" style={{ justifySelf: "start" }} onClick={() => setOpen(true)}>Add an Arabic version</button>;
  }
  async function draft() {
    if (!translate || !a.text?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const text = await translate(a.text, "text", trigger, dialect);
      const subject = emailToo && a.email_subject?.trim() ? await translate(a.email_subject, "subject", trigger, dialect) : a.email_subject_ar;
      onChange({ ...a, text_ar: text, email_subject_ar: subject });
    } catch (err) {
      setError(err instanceof Error ? err.message : "The AI couldn't translate it. Try again or write it yourself.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="arabic-version">
      <div className="inline small">
        <b>Arabic version</b>
        <span className="muted">for customers who write in Arabic</span>
        <span className="spacer" />
        {translate && (
          <>
            <select aria-label="Arabic style" value={dialect} onChange={(e) => setDialect(e.target.value)} style={{ width: "auto" }}>
              {DIALECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" className="secondary" disabled={busy || !a.text?.trim()} onClick={draft}>{busy ? "Writing…" : a.text_ar ? "Rewrite with AI" : "Write it with AI"}</button>
          </>
        )}
        <button type="button" className="link" onClick={() => { onChange({ ...a, text_ar: "", email_subject_ar: "" }); setOpen(false); }}>Remove</button>
      </div>
      {error && <p className="notice-text small">{error}</p>}
      <TextWithVars label="Message in Arabic" value={a.text_ar ?? ""} onChange={(v) => onChange({ ...a, text_ar: v })} vars={vars} multiline max={1000} dir="rtl" />
      {emailToo && <TextWithVars label="Email subject in Arabic" value={a.email_subject_ar ?? ""} onChange={(v) => onChange({ ...a, email_subject_ar: v })} vars={vars} max={150} dir="rtl" />}
    </div>
  );
}
